import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { publicCloudShareApi } from '@/services/public-cloud-share.api';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field, Spinner } from '@/components/ui';
import { LogoMark } from '@/components/logo-mark';

const UNITS = ['B', 'KB', 'MB', 'GB'];

function formatBytes(bytes: number): string {
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${UNITS[unitIndex]}`;
}

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * `/s/:token` — opening a Mi Nube share link. Reached with no session (a
 * public link) or with one (a restricted link this browser happens to be
 * signed into) — the same "works regardless of auth state" posture
 * `GivePage` documents for itself, mounted outside both app shells.
 */
export function PublicSharePage() {
  const { token = '' } = useParams();
  const { t } = useI18n();
  const { isAuthenticated } = useAuth();

  const [password, setPassword] = useState('');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const info = useQuery({
    queryKey: ['cloud', 'public-share', token],
    queryFn: ({ signal }) => publicCloudShareApi.getInfo(token, signal),
  });

  const unlock = useMutation({
    mutationFn: () =>
      info.data?.requiresSignIn
        ? filesApi.unlockShareAsViewer(token, password || undefined)
        : publicCloudShareApi.unlock(token, password || undefined),
    onSuccess: (result) => {
      setError(null);
      setDownloadUrl(result.url);
      window.open(result.url, '_blank', 'noopener');
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  if (info.isPending) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <Spinner label={t('common.states.loading')} />
        </div>
      </main>
    );
  }

  if (info.isError) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <LogoMark />
          <Alert tone="danger">{errorMessage(info.error, t)}</Alert>
        </div>
      </main>
    );
  }

  const share = info.data;
  const needsSignIn = share.requiresSignIn && !isAuthenticated;

  return (
    <main className="auth-page">
      <div className="auth-card">
        <LogoMark />
        <h1>{share.fileName}</h1>
        <p className="page-count" dir="ltr">
          {formatBytes(share.sizeBytes)}
        </p>

        {error && <Alert tone="danger">{error}</Alert>}

        {downloadUrl ? (
          <Alert tone="success">{t('files.share.publicOpened')}</Alert>
        ) : needsSignIn ? (
          <>
            <Alert tone="warning">{t('files.share.publicRequiresSignIn')}</Alert>
            <Link to="/login" className="btn" data-variant="primary">
              {t('auth.signIn')}
            </Link>
          </>
        ) : (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              unlock.mutate();
            }}
          >
            {share.requiresPassword && (
              <Field
                label={t('files.share.password')}
                name="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoFocus
                dir="ltr"
              />
            )}
            <Button type="submit" variant="primary" loading={unlock.isPending}>
              {t('files.share.publicOpen')}
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}
